<template>
  <div class="product-features__wrapper">
    <div
      v-if="productFeatures.length > 1"
      class="product-features__accordion-wrapper"
    >
      <div
        v-for="(productFeature, productFeatureIndex) in productFeatures"
        :key="`${productFeatureIndex}_productFeature`"
        class="product-features__accordion"
        :class="{
          'product-features__accordion--open': isAccordionOpen(productFeatureIndex),
          'product-features__accordion--closed': !isAccordionOpen(productFeatureIndex),
        }"
        :aria-label="productFeature.headline"
      >
        <div
          :class="`product-features__accordion-headline product-features-list--id-${productFeatureIndex}`"
          @click="toggleClass"
          role="heading"
        >
          {{ productFeature.headline }}
        </div>
        <product-features-list
          :scroll-less-element="`product-features-list--id-${productFeatureIndex}`"
          :features="productFeature.features"
          :max-height="featuresHeight"
          :show-more-link="false"
        />
      </div>
    </div>
    <template
      v-else-if="productFeatures.length === 1"
    >
      <div class="product-features__accordion-wrapper">
        <div class="product-features__accordion product-features__accordion--no-arrow">
          <div
            v-if="productStore.productDetails.isSet"
            class="product-features__accordion-headline"
            role="heading"
          >
            {{ productFeatures[0].headline }}
          </div>
          <product-features-list
            scroll-less-element="product-details__headline--product-features"
            :features="productFeatures[0].features"
            :max-height="featuresHeight"
            :show-more-link="true"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import {Options, Vue} from 'vue-property-decorator';
  import productFeaturesList from '../../../bps/components/productFeaturesList/productFeaturesList.vue'
  import productStore, {ProductStore} from '../../../bps/stores/product/ProductStore';
  import LazyLoad from '../../../bps/components/lazyLoad/lazyLoad.vue';
  import breakpoints from '../../../bps/variables/js/breakpoints.js';
  import {ProductAttributeName} from '../../../bps/types/ProductAttributeName';
  import diContainer from '../../../bps/utilities/js/diContainer/diContainer';
  import {ProductFeaturesSettings} from '../../../bps/components/productFeatures/productFeaturesSettings';
  import {ProductFeatures, MobileProductFeatures} from '../../../bps/types/ProductFeatures';
  const _debounce = require('lodash/debounce');
  const _cloneDeep = require('lodash/cloneDeep');

  const INITIAL_FEATURES_HEIGHT = 360

  @Options({
    name: 'product-features',
    components: {
      productFeaturesList,
      LazyLoad
    }
  })
  export default class ProductFeaturesClass extends Vue {
    productStore: ProductStore = productStore;
    windowWidth: number = window.innerWidth;
    featuresHeight: number = INITIAL_FEATURES_HEIGHT;
    settings: ProductFeaturesSettings = diContainer.get('productFeaturesSettings');

    mobileOrder = {
      [ProductAttributeName.ARTIKELNUMMER]: {
        order: 1,
      },
      [ProductAttributeName.REBSORTEN]: {
        order: 2,
      },
      [ProductAttributeName.ALKOHOLGEHALT]: {
        order: 3,
      },
      [ProductAttributeName.RESTSUESSE]: {
        order: 4,
      },
      [ProductAttributeName.SAEUREGEHALT]: {
        order: 5,
      },
      [ProductAttributeName.ANSCHRIFT_HERSTELLER]: {
        order: 6,
      },
    }

    mounted() {
      window.addEventListener('resize', _debounce(this.onResize, 50, {
        maxWait: 50
      }))
    }

    destroyed() {
      window.removeEventListener('resize', this.onResize)
    }

    get productFeatures(): ProductFeatures[] {
      if (this.windowWidth < breakpoints.phoneMd) {
        return this.getOrderedFeaturesForMobile();
      }
      return this.productStore.productDetails.productFeatures;
    }

    isAccordionOpen(productFeatureIndex) {
      return productFeatureIndex === 0 && !this.isMobile() && this.settings.isInitiallyOpen;
    }

    isMobile() {
      return this.windowWidth < breakpoints.tabletSm;
    }

    toggleClass(event) {
      const accordion = event.target.closest('.product-features__accordion');
      if (!accordion) {
        return;
      }
      const featureList = accordion.querySelector('.product-features-list');
      featureList.style.maxHeight = featureList.querySelector('.product-features-list__list').offsetHeight + 'px';
      if (accordion.classList.contains('product-features__accordion--open')) {
        featureList.style.maxHeight = '0';
        accordion.classList.remove('product-features__accordion--open');
        accordion.classList.add('product-features__accordion--closed');
      } else {
        accordion.classList.add('product-features__accordion--open');
        accordion.classList.remove('product-features__accordion--closed');
      }
    }

    getOrderedFeaturesForMobile(): ProductFeatures[] {
      const productFeatures: MobileProductFeatures[] = _cloneDeep(this.productStore.productDetails.productFeatures);

      productFeatures
        .forEach(featureItem => {
          featureItem.features
            .forEach(feature => {
              feature.values.forEach(value => {
                value.mobileOrder = this.mobileOrder[value.name] ? this.mobileOrder[value.name].order : 99
                if (value.name === ProductAttributeName.REBSORTEN) {
                  this.calculateHeightBasedOnVarietyAmount(value.value);
                }
              });
            })

          featureItem.features
            .sort((a, b) => a.values[0].mobileOrder - b.values[0].mobileOrder)
        })

      return productFeatures;
    }

    onResize() {
      this.windowWidth = window.innerWidth
    }

    calculateHeightBasedOnVarietyAmount(value: string) {
      const varietyAmount = value.split('<br/>').length;
      this.featuresHeight = varietyAmount > 1 ? INITIAL_FEATURES_HEIGHT + (varietyAmount * 15) : INITIAL_FEATURES_HEIGHT;
    }
  }
</script>
